import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Alert,
  Platform,
  SafeAreaView,
  ScrollView,
  Share,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { ActivityIndicator, Button, ProgressBar, Text } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import {
  deriveLegacyMasterKey,
  readStoredLegacyMasterKey,
} from '../../services/encryption/legacy/LegacyDecryption';
import {
  PBKDF2_ITERATIONS,
  deriveKey,
} from '../../services/encryption/DocumentEncryptionV2';
import { getOrCreateUserSalt } from '../../services/encryption/UserSalt';
import { vaultService } from '../../services/vaultService';
import {
  MigrationFailure,
  MigrationSummary,
  detectLegacyData,
  exportFailedItems,
  migrateAll,
} from '../../services/migration/VaultMigrationService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('MigrationScreen');

type Phase =
  | 'checking'
  | 'nothing_to_migrate'
  | 'prompt_code'
  | 'migrating'
  | 'success'
  | 'partial_failure'
  | 'error';

const MigrationScreen: React.FC = () => {
  // `any` here: the outer root stack param list isn't typed consistently in
  // this codebase; the only navigation we need is `reset` to 'Dashboard'.
  const navigation = useNavigation<NativeStackNavigationProp<any>>();

  const [phase, setPhase] = useState<Phase>('checking');
  const [itemCount, setItemCount] = useState(0);
  const [secretCode, setSecretCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [summary, setSummary] = useState<MigrationSummary | null>(null);

  // Latest-value ref so announce() doesn't fight stale closures in callbacks
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    (async () => {
      try {
        const plan = await detectLegacyData();
        if (plan.itemsToMigrate.length === 0) {
          setPhase('nothing_to_migrate');
          return;
        }
        setItemCount(plan.itemsToMigrate.length);
        setPhase('prompt_code');
      } catch (err) {
        logger.error('detectLegacyData failed', err);
        setPhase('error');
      }
    })();
  }, []);

  const announce = useCallback((msg: string) => {
    AccessibilityInfo.announceForAccessibility?.(msg);
  }, []);

  const startMigration = useCallback(async () => {
    setSubmitError(null);
    if (!secretCode || secretCode.length < 8) {
      setSubmitError('Please enter your secret code (at least 8 characters).');
      return;
    }

    setPhase('migrating');
    setProgress(0);
    setProgressLabel(`Preparing to upgrade ${itemCount} item${itemCount === 1 ? '' : 's'}...`);
    announce('Migration starting. Do not close the app.');

    try {
      // 1. Determine the legacy master key. Prefer the stored one (it's the
      // actual key that encrypted the data) but cross-check against what the
      // secret code derives so we can reject a wrong code up front.
      const derivedLegacy = deriveLegacyMasterKey(secretCode);
      const storedLegacy = await readStoredLegacyMasterKey();
      const legacyMasterKey = storedLegacy ?? derivedLegacy;
      if (storedLegacy && storedLegacy !== derivedLegacy) {
        logger.warn('stored legacy master key does not match derivation from secret code');
        // Fall through: use the stored key. This can happen if the user
        // changed secret codes in the past. The secret-code input is still
        // required because the NEW key derivation uses it below.
      }

      // 2. Derive the new v2 master key from the same secret code using the
      // per-user salt (generated now if absent), then persist it.
      const salt = await getOrCreateUserSalt();
      const newMasterKey = await deriveKey(secretCode, salt, PBKDF2_ITERATIONS);
      await vaultService.storeMasterRecoveryKey(newMasterKey);

      // 3. Run the migration, streaming progress into the UI.
      const result = await migrateAll(legacyMasterKey, newMasterKey, {
        onProgress: ({ currentIndex, total, lastItem }) => {
          const pct = total > 0 ? currentIndex / total : 1;
          setProgress(pct);
          const label = `Migrating item ${currentIndex} of ${total}...`;
          setProgressLabel(label);
          if (currentIndex === total) {
            announce(`Upgrade complete. ${total - 1} items migrated.`);
          } else if (lastItem) {
            // Announce every few items so screen readers aren't overwhelmed
            if (currentIndex % 5 === 0) {
              announce(label);
            }
          }
        },
      });

      setSummary(result);
      if (result.failed === 0) {
        setPhase('success');
        announce(`All ${result.migrated} items migrated successfully.`);
      } else {
        setPhase('partial_failure');
        announce(
          `${result.migrated} of ${result.total} items migrated. ${result.failed} could not be migrated.`,
        );
      }
    } catch (err) {
      logger.error('migration failed', err);
      setSubmitError(err instanceof Error ? err.message : 'Unknown error.');
      setPhase('error');
    }
  }, [secretCode, itemCount, announce]);

  const exportFailures = useCallback(async (failures: MigrationFailure[]) => {
    const payload = exportFailedItems(failures);
    try {
      await Share.share({
        message: payload,
        title: 'MyFinalKey — Un-migrated items',
      });
    } catch (err) {
      logger.error('Share failed', err);
      Alert.alert('Export failed', 'Unable to open the share sheet. Please try again.');
    }
  }, []);

  const continueToApp = useCallback(() => {
    navigation.reset({ index: 0, routes: [{ name: 'Dashboard' as never }] });
  }, [navigation]);

  const renderBody = () => {
    switch (phase) {
      case 'checking':
        return (
          <View style={styles.center}>
            <ActivityIndicator accessibilityLabel="Checking for items to upgrade" />
            <Text style={styles.bodyText}>Checking your vault...</Text>
          </View>
        );

      case 'nothing_to_migrate':
        return (
          <View style={styles.center}>
            <Text variant="titleMedium">Your vault is up to date.</Text>
            <Button mode="contained" onPress={continueToApp} style={styles.primaryButton}>
              Continue
            </Button>
          </View>
        );

      case 'prompt_code':
        return (
          <View>
            <Text variant="titleLarge" style={styles.title}>
              Security upgrade
            </Text>
            <Text style={styles.bodyText}>
              We're upgrading your vault to stronger encryption. This re-encrypts
              the {itemCount} item{itemCount === 1 ? '' : 's'} already stored on this
              device. Your data will be preserved.
            </Text>
            <Text style={styles.bodyText}>
              Enter your secret code to continue. This usually takes under a minute.
            </Text>
            <TextInput
              style={styles.input}
              secureTextEntry
              autoCapitalize="none"
              autoCorrect={false}
              placeholder="Secret code"
              placeholderTextColor="#8a8a8a"
              accessibilityLabel="Your secret code"
              value={secretCode}
              onChangeText={setSecretCode}
              returnKeyType="go"
              onSubmitEditing={startMigration}
            />
            {submitError ? (
              <Text style={styles.errorText} accessibilityLiveRegion="polite">
                {submitError}
              </Text>
            ) : null}
            <Button mode="contained" onPress={startMigration} style={styles.primaryButton}>
              Upgrade my vault
            </Button>
            <Button
              mode="text"
              onPress={() =>
                Alert.alert(
                  'Export before upgrade?',
                  'You can export a raw data dump of anything encrypted with the legacy scheme. Support can help recover items using this dump if upgrade ever fails.',
                  [
                    { text: 'Cancel', style: 'cancel' },
                    {
                      text: 'Export',
                      onPress: async () => {
                        // No failures yet — export whatever's detected
                        const plan = await detectLegacyData();
                        const placeholders: MigrationFailure[] = plan.itemsToMigrate.map(i => ({
                          id: i.id,
                          source: i.source,
                          stage: 'read',
                          error: 'pre-migration export',
                        }));
                        await exportFailures(placeholders);
                      },
                    },
                  ],
                )
              }
            >
              Export my data first
            </Button>
          </View>
        );

      case 'migrating':
        return (
          <View>
            <Text variant="titleLarge" style={styles.title}>
              Upgrading your vault
            </Text>
            <Text style={styles.bodyText}>{progressLabel}</Text>
            <ProgressBar
              progress={progress}
              accessibilityLabel={progressLabel}
              style={styles.progressBar}
            />
            <Text style={styles.bodyTextSmall}>
              Your data is being re-encrypted with stronger protection. Do not
              close the app.
            </Text>
          </View>
        );

      case 'success':
        return (
          <View style={styles.center}>
            <Text variant="titleLarge" style={styles.title}>
              Upgrade complete
            </Text>
            <Text style={styles.bodyText}>
              All {summary?.migrated ?? 0} items migrated successfully. Your vault
              is now using FinalKey's new security scheme.
            </Text>
            <Button mode="contained" onPress={continueToApp} style={styles.primaryButton}>
              Continue
            </Button>
          </View>
        );

      case 'partial_failure':
        return (
          <View>
            <Text variant="titleLarge" style={styles.title}>
              Upgrade partly complete
            </Text>
            <Text style={styles.bodyText}>
              {summary?.migrated ?? 0} of {summary?.total ?? 0} items migrated
              successfully. The following {summary?.failed ?? 0} item
              {summary?.failed === 1 ? '' : 's'} couldn't be migrated:
            </Text>
            {summary?.failures.map(f => (
              <Text key={`${f.source}:${f.id}`} style={styles.bodyTextSmall}>
                {'• '}
                {f.source} / {f.id} — {f.stage}: {f.error}
              </Text>
            ))}
            <Text style={styles.bodyText}>
              Tap Export to save these items so you don't lose them. After export,
              you can continue to the app (failed items remain in legacy format)
              or contact support.
            </Text>
            <Button
              mode="contained"
              onPress={() => exportFailures(summary?.failures ?? [])}
              style={styles.primaryButton}
            >
              Export failed items
            </Button>
            <Button mode="text" onPress={continueToApp}>
              Continue anyway
            </Button>
          </View>
        );

      case 'error':
      default:
        return (
          <View style={styles.center}>
            <Text variant="titleLarge" style={styles.title}>
              Something went wrong
            </Text>
            <Text style={styles.bodyText}>
              {submitError ?? 'Please close and re-open the app. No data has been lost.'}
            </Text>
            <Button mode="outlined" onPress={() => setPhase('prompt_code')}>
              Try again
            </Button>
          </View>
        );
    }
  };

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.content}>{renderBody()}</ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F3F6F8' },
  content: { padding: 20, flexGrow: 1 },
  center: { alignItems: 'center', justifyContent: 'center', flexGrow: 1 },
  title: { marginBottom: 12 },
  bodyText: { marginVertical: 8, fontSize: 15, lineHeight: 22 },
  bodyTextSmall: { marginVertical: 4, fontSize: 13, color: '#555' },
  input: {
    borderWidth: 1,
    borderColor: '#c9c9c9',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginTop: 12,
    backgroundColor: '#fff',
    ...(Platform.OS === 'ios' ? {} : { paddingVertical: 8 }),
  },
  errorText: { color: '#b00020', marginTop: 8 },
  progressBar: { height: 8, borderRadius: 4, marginVertical: 12 },
  primaryButton: { marginTop: 16 },
});

export default MigrationScreen;
